import { useDispatch, useSelector } from "react-redux";
import useIndustry from "./useIndustry";
import { useContext, useState } from "react";
import { AppContext } from "context/appContext";
import { setAllCompanies } from "slices/companySlice";
import api from "configs/api";
import useAuth from "./useAuth";
import { RootState } from "configs/store";

interface CompanyField {
  placeholder: string;
  name: string;
  type: string;
  size: string;
  optionsLabel?: string;
  options?: any[];
}

interface Company {
  _id: string;
  companyName: string;
  email: string;
  imageURL?: string;
  name: string;
  role: string;
  status: boolean;
  employees: number;
  createdAt: string;
  [key: string]: any;
}

interface CompanyFilter {
  page?: number;
  limit?: number;
  [key: string]: any;
}

export default function useCompany() {
  const authBaseUrl = "auth/";
  const { allCompanies } = useSelector((state: RootState) => state.company);
  const dispatch = useDispatch();

  // States
  const [addCompanyLoading, setAddCompanyLoading] = useState<boolean>(false);
  const [isCompanyUpdate, setIsCompanyUpdate] = useState<boolean>(false);
  const [showAddCompany, setShowAddCompany] = useState<boolean>(false);

  // Hooks
  const { industries } = useIndustry();
  const { showAlert } = useContext(AppContext);
  const { emailDubCheck } = useAuth();

  // Variables
  const companyFieldsAdmin: CompanyField[] = [
    {
      placeholder: "First Name",
      name: "firstname",
      type: "text",
      size: "medium",
    },
    {
      placeholder: "Last Name",
      name: "lastname",
      type: "text",
      size: "medium",
    },
    {
      placeholder: "Company Email",
      name: "email",
      type: "email",
      size: "medium",
    },
    {
      placeholder: "Phone Number",
      name: "telephone1",
      type: "text",
      size: "medium",
    },
    {
      placeholder: "Company Name",
      name: "companyName",
      type: "text",
      size: "medium",
    },
    {
      placeholder: "Industry",
      name: "industry",
      type: "dropdown",
      size: "medium",
      optionsLabel: "name",
      options: industries,
    },
  ].filter((item) => item) as CompanyField[];

  const companyProfileTableColumns = [
    { name: "company", align: "left" as const },
    { name: "function", align: "left" as const },
    { name: "director", align: "left" as const },
    { name: "status", align: "left" as const },
    { name: "employees", align: "left" as const },
    { name: "created", align: "left" as const },
    { name: "action", align: "center" as const },
  ];

  // Functions
  const addCompany = async (data: any): Promise<void> => {
    setAddCompanyLoading(true);
    try {
      const emailAvailable = await emailDubCheck(data.email);
      if (!emailAvailable) {
        showAlert("Email already in use.", "error", "Fields Error");
        setAddCompanyLoading(false);
        return;
      }
      const response = await api.post(`${authBaseUrl}user/register`, {
        ...data,
        accountRole: "company",
      });
      if (response.data?.status) {
        showAlert("Company account created successfully.", "success", "Registration Success!");
        getAllCompanies({});
      } else {
        showAlert(response.data.message, "error", "Registration Failed!");
      }
    } catch (err: any) {
      const message = err.response
        ? err.response.data?.message
          ? err.response.data?.message
          : err.response.data
        : err.message || 'Server error';
      console.log(err.message);
      showAlert(
        "Looks like there is some issue with the server. Please try again later.",
        "error",
        "Server Error"
      );
    } finally {
      setShowAddCompany(false);
      setAddCompanyLoading(false);
    }
  };

  const getAllCompanies = async (filter: CompanyFilter = {}): Promise<void> => {
    try {
      const response = await api.get(`${authBaseUrl}company`, { params: filter });
      if (response.data.status) {
        dispatch(setAllCompanies(response.data.result || []));
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  return {
    addCompany,
    getAllCompanies,
    allCompanies: allCompanies as Company[],
    companyFieldsAdmin,
    companyProfileTableColumns,
    addCompanyLoading,
    setAddCompanyLoading,
    isCompanyUpdate,
    setIsCompanyUpdate,
    showAddCompany,
    setShowAddCompany,
  };
}

